import React, { useState } from 'react';
import { displayDate } from "src/utils/dateHandler.util";
import { CoachRecording } from "../types/coach-recording.entity";
import { formatScreamingSnakeCase, getLangNameByLangCode } from "src/utils/util";
import ViewRecordModal from "./view-record.modal";
import { Button } from "src/components/Form/button";
import { useUpdateCoachApplicationStatusMutation } from "../coach-recordings.service";
import { useAppDispatch } from "src/store";
import { showToast } from "src/store/toast.store";
import { useDownloadUrl } from "src/customHooks/useDownloadUr";
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from 'sweetalert2';

type Props = {
  data: CoachRecording[];
  onSelectRecord?: (recordId: string) => void;
  selectedRecords?: string[];
};

const CoachRecordingCards: React.FC<Props> = ({ data, onSelectRecord, selectedRecords = [] }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [updateStatus] = useUpdateCoachApplicationStatusMutation();

  const handleStatusUpdate = async (recordId: string, action: "APPROVE" | "REJECT") => {
    console.log('handleStatusUpdate called with:', { recordId, action });
    
    const actionText = action === "APPROVE" ? "approve" : "reject";
    const actionTextPast = action === "APPROVE" ? "approved" : "rejected";
    
    try {
      // Show confirmation dialog
      console.log('Showing confirmation dialog...');
      const result = await Swal.fire({
        title: `Confirm ${actionText}`,
        text: `Are you sure you want to ${actionText} this coach application?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: action === "APPROVE" ? '#10b981' : '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: `Yes, ${actionText}`,
        cancelButtonText: 'Cancel'
      });
      
      console.log('Confirmation result:', result);

      if (result.isConfirmed) {
        try {
          // Show loading state
          console.log('Showing loading state...');
          Swal.fire({
            title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)}ing...`,
            text: `Please wait while we ${actionText} the coach application...`,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          console.log('Updating status...');
          await updateStatus({ id: recordId, action }).unwrap();
          
          // Show success message
          console.log('Showing success message...');
          Swal.fire({
            title: 'Success! 🎉',
            text: `Coach application ${actionTextPast} successfully`,
            icon: 'success',
            confirmButtonColor: '#3b82f6'
          });
          
          // Also show toast notification
          dispatch(
            showToast({
              type: "success",
              message: `Coach ${actionTextPast} successfully`,
            })
          );
        } catch (error) {
          console.error('Error updating status:', error);
          // Show error message
          Swal.fire({
            title: 'Error! ❌',
            text: `Failed to ${actionText} coach application. Please try again.`,
            icon: 'error',
            confirmButtonColor: '#ef4444'
          });
          
          // Also show toast notification
          dispatch(
            showToast({
              type: "error",
              message: "Failed to update status",
            })
          );
        }
      }
    } catch (error) {
      console.error('Error in handleStatusUpdate:', error);
      // Show error message
      Swal.fire({
        title: 'Error! ❌',
        text: `Failed to show confirmation dialog. Please try again.`,
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'UNDER_REVIEW':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'USER_CANCELED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getReviewTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'AI':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'MANUAL':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 animate-fade-in-up">
        <div className="relative mb-6">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        <h4 className="text-xl font-bold text-gray-800 mb-2">No Coach Recordings</h4>
        <p className="text-gray-600">No recordings available at the moment</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
             {data.map((record, index) => (
         <div 
           key={record.id} 
                       className={`group rounded-2xl p-6 border border-gray-200/60 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 hover:scale-[1.02] animate-fade-in-up backdrop-blur-sm relative overflow-hidden ${
              index % 2 === 0 
                ? 'bg-gradient-to-br from-white via-white to-blue-50/30' 
                : 'bg-gradient-to-br from-blue-50/20 via-blue-50/30 to-blue-100/40'
            }`}
           style={{ animationDelay: `${index * 0.1}s` }}
         >
          {/* Animated Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          {/* Header Section */}
          <div className="flex items-center justify-between mb-6 relative z-10">
            <div className="flex items-center space-x-4">
                             {/* Selection Checkbox - Only for PENDING and UNDER_REVIEW records */}
               {onSelectRecord && (record.manualReviewStatus === "PENDING" || record.manualReviewStatus === "UNDER_REVIEW") && (
                 <div className="flex items-center">
                   <input
                     type="checkbox"
                     checked={selectedRecords?.includes(record.id) || false}
                     onChange={() => onSelectRecord(record.id)}
                     className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                   />
                 </div>
               )}
              
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                  <svg className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                  </svg>
                </div>
                {record.manualReviewStatus === 'PENDING' && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full border-2 border-white animate-pulse"></div>
                )}
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300">
                  {record.email || 'Anonymous User'}
                </h4>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-sm font-semibold text-blue-600">{getLangNameByLangCode(record.langCode)}</span>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  ID: {record.userId}
                </div>
              </div>
            </div>
            
            {/* Status and Review Type Badges */}
            <div className="flex items-center space-x-3">
              <div className={`px-4 py-2 text-xs font-bold rounded-xl border-2 ${getStatusBadgeColor(record.manualReviewStatus)} shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105`}>
                {formatScreamingSnakeCase(record.manualReviewStatus)}
              </div>
              <div className={`px-4 py-2 text-xs font-bold rounded-xl border-2 ${getReviewTypeBadgeColor(record.reviewType)} shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center space-x-1`}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  {record.reviewType === 'AI' ? (
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                  )}
                </svg>
                <span>{record.reviewType}</span>
              </div>
            </div>
          </div>
          
          {/* Divider with Gradient */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-6"></div>
          
          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 relative z-10 mb-6">
            <div className="flex items-center p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Created</span>
                <p className="font-semibold text-gray-900">{displayDate(record.createdAt)}</p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Retry Count</span>
                <p className="font-semibold text-gray-900">{record.retryCount}</p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-gray-50/50 rounded-xl border border-gray-200/60 hover:bg-gray-100/50 transition-all duration-300">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Language</span>
                <p className="font-semibold text-gray-900">{getLangNameByLangCode(record.langCode)}</p>
              </div>
            </div>

            {/* Score Information - if available */}
            {record.score && (
              <div className="flex items-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200/60 hover:bg-purple-100/50 transition-all duration-300">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">AI Score</span>
                  <p className="font-semibold text-gray-900">
                    {Math.round((record.score.accuracyScore + record.score.fluencyScore + record.score.completenessScore + record.score.pronunciationScore) / 4)}%
                  </p>
                </div>
              </div>
            )}
          </div>



          {/* Audio Player Section */}
          <div className="mb-6 relative z-10">
            {record.recordingUrl ? (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200/60">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4.617-3.793a1 1 0 011.383 0zM12.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-4.929 9.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 12c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0117 12a5.983 5.983 0 01-2.757 5.243 1 1 0 01-1.415-1.415A3.987 3.987 0 0015 12a3.987 3.987 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h5 className="font-semibold text-gray-900">Audio Recording</h5>
                </div>
                <AudioPlayer url={record.recordingUrl} />
              </div>
            ) : (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200/60">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4.617-3.793a1 1 0 011.383 0zM12.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-4.929 9.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 12c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0117 12a5.983 5.983 0 01-2.757 5.243 1 1 0 01-1.415-1.415A3.987 3.987 0 0015 12a3.987 3.987 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h5 className="font-semibold text-gray-600">No Audio Recording</h5>
                </div>
                <div className="text-center text-sm text-gray-500">
                  ⚠️ No recording file available for this submission
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons Section */}
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center space-x-3">
              {/* View Record Button - Always visible */}
              <Button 
                type="button" 
                onClick={() => setOpenId(record.id)}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
              >
                View Record
              </Button>
            </div>

            {/* Approve/Reject Buttons for PENDING records */}
            {record.manualReviewStatus === "PENDING" && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleStatusUpdate(record.id, "APPROVE")}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 flex items-center space-x-2"
                  title="Approve Coach"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => handleStatusUpdate(record.id, "REJECT")}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 flex items-center space-x-2"
                  title="Reject Coach"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span>Reject</span>
                </button>
              </div>
            )}

            {/* Under Review Actions */}
            {record.manualReviewStatus === "UNDER_REVIEW" && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleStatusUpdate(record.id, "APPROVE")}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 flex items-center space-x-2"
                  title="Approve Coach"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => handleStatusUpdate(record.id, "REJECT")}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 flex items-center space-x-2"
                  title="Reject Coach"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span>Reject</span>
                </button>
              </div>
            )}
          </div>
          
          {/* Subtle Border Animation */}
          <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-blue-200/50 transition-all duration-500"></div>
        </div>
      ))}

      {/* View Record Modal */}
      {openId && <ViewRecordModal id={openId} handleClose={() => setOpenId(null)} />}
      
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

// Audio Player Component (embedded)
const AudioPlayer: React.FC<{ url: string }> = ({ url }) => {
  const { fileUrl, loading, error } = useDownloadUrl(url);

  if (loading) {
    return (
      <div className="text-center">
        <div className="flex items-center justify-center space-x-2 text-blue-600">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Loading audio...</span>
        </div>
      </div>
    );
  }

  if (error || !fileUrl) {
    return (
      <div className="text-center">
        <div className="text-sm text-red-600">⚠️ Failed to load audio</div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <audio controls className="w-full">
        <source src={fileUrl} type="audio/mp3" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default CoachRecordingCards;
